import { z } from "zod";
import type {
  CreatePlanInvitationRequest,
  ExpertPlan,
  PlanBillingOption,
  PlanInvitation,
  PlanInvitationStatus,
  PlanInvitationUserSummary,
} from "@shopexperts/legacy-api";
import { formatBillingInterval, formatOptionAmount } from "./billingOptionForm";

export interface PaymentInvitationFormValues {
  v2_plan_id: string;
  v2_plan_billing_option_id: string;
  expires_at: string | InvitationExpiryDate;
}

export interface InvitationExpiryDate {
  year: number;
  month: number;
  day: number;
}

function invitationExpiryTimestamp(value: PaymentInvitationFormValues["expires_at"]): number | null {
  if (!value) return null;
  if (typeof value === "string") {
    // A bare date means "valid through that day", same as the object form.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
      return endOfDayTimestamp(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]));
    }
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.NaN;
  }
  return endOfDayTimestamp(value.year, value.month, value.day);
}

function endOfDayTimestamp(year: number, month: number, day: number): number {
  const date = new Date(year, month - 1, day, 23, 59, 59, 999);
  // The Date constructor normalizes out-of-range parts (Feb 30 -> Mar 2);
  // treat any input the round trip doesn't preserve as invalid.
  const isRealCalendarDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealCalendarDate && Number.isFinite(date.getTime()) ? date.getTime() : Number.NaN;
}

/**
 * Normalize a date-picker value to the string the form stores. Keeping form
 * state to strings matters: UiForm and vee-validate two-way-sync values with
 * deep watchers and clone on the way through, so an object value gets a fresh
 * identity every cycle and the sync never converges (SE-827 froze the tab).
 */
export function invitationExpiryFormValue(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const date = value as Partial<InvitationExpiryDate>;
  if (typeof date.year === "number" && typeof date.month === "number" && typeof date.day === "number") {
    return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
  }
  return "";
}

export const paymentInvitationFormSchema = z.object({
  v2_plan_id: z.string().min(1, "Choose a plan"),
  v2_plan_billing_option_id: z.string().min(1, "Choose a billing option"),
  expires_at: z.union([
    z.literal(""),
    z.string(),
    z.object({ year: z.number(), month: z.number(), day: z.number() }),
  ]).refine((value) => {
    const timestamp = invitationExpiryTimestamp(value);
    return timestamp === null || (Number.isFinite(timestamp) && timestamp > Date.now());
  }, "Expiry must be in the future"),
});

export function defaultPaymentInvitationFormValues(): PaymentInvitationFormValues {
  return {
    v2_plan_id: "",
    v2_plan_billing_option_id: "",
    expires_at: "",
  };
}

export function formValuesToCreateInvitationPayload(
  values: PaymentInvitationFormValues,
): CreatePlanInvitationRequest {
  const expiryTimestamp = invitationExpiryTimestamp(values.expires_at);
  return {
    v2_plan_id: Number(values.v2_plan_id),
    v2_plan_billing_option_id: Number(values.v2_plan_billing_option_id),
    ...(expiryTimestamp !== null && Number.isFinite(expiryTimestamp)
      ? { expires_at: new Date(expiryTimestamp).toISOString() }
      : {}),
  };
}

export function planMatchesExpertType(plan: ExpertPlan, expertType: string): boolean {
  return plan.is_active && (plan.expert_type === "all" || plan.expert_type === expertType);
}

export function eligibleInvitationModes(option: PlanBillingOption): string[] {
  if (!option.is_active) return [];
  return [...new Set(
    (option.stripe_prices ?? [])
      .filter((price) => price.is_current)
      .map((price) => price.mode),
  )];
}

export function isInvitationOptionEligible(option: PlanBillingOption): boolean {
  return eligibleInvitationModes(option).length > 0;
}

export function invitationOptionSelectLabel(plan: ExpertPlan, option: PlanBillingOption): string {
  const modes = eligibleInvitationModes(option).map((mode) => mode === "live" ? "Live" : "Test").join(" / ");
  return `${plan.name} - ${option.label} · ${formatOptionAmount(option)} · ${formatBillingInterval(option)}${modes ? ` · ${modes}` : ""}`;
}

export interface InvitationStatusDisplay {
  label: string;
  variant: "warn" | "success" | "neutral" | "highlight";
  bgColorClass?: string;
  textColorClass?: string;
}

const INVITATION_STATUS_DISPLAY: Record<PlanInvitationStatus, InvitationStatusDisplay> = {
  pending: { label: "Pending", variant: "warn" },
  redeemed: { label: "Redeemed", variant: "success" },
  revoked: {
    label: "Revoked",
    variant: "neutral",
    bgColorClass: "bg-red-100",
    textColorClass: "text-red-800",
  },
  expired: { label: "Expired", variant: "neutral" },
  superseded: { label: "Superseded", variant: "highlight" },
};

export function invitationStatusDisplay(status: PlanInvitationStatus): InvitationStatusDisplay {
  return INVITATION_STATUS_DISPLAY[status];
}

export function formatInvitationActor(actor: PlanInvitationUserSummary | null | undefined): string {
  if (!actor) return "Unknown admin";
  const name = [actor.first_name, actor.last_name].filter(Boolean).join(" ").trim();
  return name || actor.email;
}

export function formatInvitationDate(value: string | null | undefined): string {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export function invitationLifecycleText(invitation: PlanInvitation): string {
  if (invitation.status === "pending") return `Expires ${formatInvitationDate(invitation.expires_at)}`;
  if (invitation.status === "redeemed") return `Redeemed ${formatInvitationDate(invitation.redeemed_at)}`;
  if (invitation.status === "revoked") {
    return `Revoked ${formatInvitationDate(invitation.revoked_at)} by ${formatInvitationActor(invitation.revoked_by)}`;
  }
  if (invitation.status === "expired") return `Expired ${formatInvitationDate(invitation.expires_at)}`;
  return invitation.superseded_by_invitation_id
    ? `Replaced by invitation #${invitation.superseded_by_invitation_id}`
    : `Superseded ${formatInvitationDate(invitation.superseded_at)}`;
}

export interface InvitationApiErrorDetails {
  status: number | null;
  message: string | null;
  checkoutExpiresAt: string | null;
}

export function parseInvitationApiError(error: unknown): InvitationApiErrorDetails {
  if (!error || typeof error !== "object") {
    return { status: null, message: error instanceof Error ? error.message : null, checkoutExpiresAt: null };
  }
  const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
  const status = typeof response?.status === "number" ? response.status : null;
  const data = response?.data;
  if (data && typeof data === "object") {
    const body = data as { message?: unknown; checkout_expires_at?: unknown };
    return {
      status,
      message: typeof body.message === "string" ? body.message : null,
      checkoutExpiresAt: typeof body.checkout_expires_at === "string" ? body.checkout_expires_at : null,
    };
  }
  return {
    status,
    message: error instanceof Error ? error.message : null,
    checkoutExpiresAt: null,
  };
}

export function isInvitationFeatureUnavailable(error: unknown): boolean {
  return parseInvitationApiError(error).status === 404;
}

export function checkoutLockMessage(error: unknown): string | null {
  const details = parseInvitationApiError(error);
  if (details.status !== 422 || !details.message?.toLowerCase().includes("active stripe checkout")) return null;
  return details.checkoutExpiresAt
    ? `Locked until ${formatInvitationDate(details.checkoutExpiresAt)} while the expert completes Checkout.`
    : "Locked while the expert has an active Stripe Checkout. Try again after that Checkout expires.";
}
